import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useTable, usePagination } from "react-table";
import axiosApi from "../Utils/axiosApi";
import "./UserListPage.css";
import HeaderMenu from "../Header/HeaderMenu";
import checkPermission from '../Utils/checkPermission';
import {
  Button,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Pagination,
  CircularProgress,
  Box,
  Typography,
  TextField,
  Grid,
  MenuItem,
} from "@mui/material";
import CreateUser from "../UserMaster/CreateUser";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '';

const UserListPage = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [userToDelete, setUserToDelete] = useState(null);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);

  // ------------------- Filters -------------------
  const [filters, setFilters] = useState({ name: "", roleId: "", companyId: "" });
  const [roles, setRoles] = useState([]);
  const [companies, setCompanies] = useState([]);

  // ------------------- Fetch Roles -------------------
  const fetchRoles = useCallback(async () => {
    try {
      const response = await axiosApi.post(`${API_BASE_URL}/role/getUserRole`, {});
      const rolesArray = Array.isArray(response.data)
        ? response.data
        : response.data.userRoles || [];
      setRoles(rolesArray);
    } catch (error) {
      console.error("Error fetching user roles:", error);
    }
  }, []);

  // ------------------- Fetch Companies -------------------
  const fetchCompanies = useCallback(async () => {
    try {
      const response = await axiosApi.post("/company/getCompany", {});
      setCompanies(response.data || []);
    } catch (err) {
      console.error("Failed to fetch companies", err);
    }
  }, []);

  // ------------------- Fetch Users -------------------
  const fetchUsers = useCallback(async (searchPayload = {}) => {
    setLoading(true);
    try {
      const payload = { ...searchPayload };
      const response = await axiosApi.post("/userManagement/getUsers", payload);
      setData(response.data || []);
    } catch (error) {
      console.error("Failed to load users:", error);
      showSnackbar("Failed to load users", "error");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchRoles();
    fetchCompanies();
    fetchUsers();
  }, [fetchRoles, fetchCompanies, fetchUsers]);

  // ------------------- Snackbar -------------------
  const showSnackbar = (message, severity = "success") => {
    setSnackbar({ open: true, message, severity });
  };

  // ------------------- Filter Handlers -------------------
  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name]: value }));
  };

  const handleSearch = () => {
  const payload = {};

  if (filters.name) payload.userName = filters.name;

 
  if (filters.roleId) payload.roleId = filters.roleId;

  if (filters.companyId) payload.companyId = filters.companyId;

  fetchUsers(payload);
};




  // ------------------- View User -------------------
  const handleViewDetails = useCallback(async (user) => {
    try {
      const response = await axiosApi.post("/userManagement/getUsers", { userIds: [user.id] });
      const details = Array.isArray(response.data) ? response.data[0] : response.data;
      setSelectedUser(details);
      setDialogOpen(true);
    } catch (err) {
      console.error("Error fetching user details:", err);
      showSnackbar("Failed to fetch user details", "error");
    }
  }, []);

  // ------------------- Delete User -------------------
  const handleDeleteClick = (userId) => {
    setUserToDelete(userId);
    setConfirmDeleteOpen(true);
  };

  const confirmDelete = async () => {
    if (!userToDelete) return;
    try {
      await axiosApi.post("/userManagement/deleteUser", { userIds: [userToDelete] });
      showSnackbar("User deleted successfully", "success");
      fetchUsers();
    } catch (error) {
      console.error("Error deleting user:", error);
      showSnackbar("Failed to delete user", "error");
    } finally {
      setConfirmDeleteOpen(false);
      setUserToDelete(null);
    }
  };

  // ------------------- Reset Password -------------------
  const handleResetPassword = useCallback(async (userId) => {
    try {
      await axiosApi.post("/userManagement/resetPassword", { userIds: [userId] });
      showSnackbar("Password reset successfully", "success");
      fetchUsers();
    } catch (error) {
      console.error("Error resetting user password:", error);
      showSnackbar("Failed to reset password", "error");
    }
  }, [fetchUsers]);

  // ------------------- Table Columns -------------------
  const columns = useMemo(() => [
	{ Header: '#', accessor: '' },
    { Header: "User Name", accessor: "userName" },
    { Header: "First Name", accessor: "firstName" },
    { Header: "Last Name", accessor: "lastName" },
    { Header: "Email", accessor: "email" },
    { Header: "Company Name", accessor: "companyName" },
    { Header: "Contact Number", accessor: "mobileNumber" },
    { Header: "User Role", accessor: "roleCode" },
    {
      Header: "Actions",
	  
      Cell: ({ row }) => (
        <div className="actions-container">
          <Button
            disabled={!checkPermission("Admin", "Users", "VIEW")}
            size="small"
            color="primary"
            variant="outlined"
            onClick={() => handleViewDetails(row.original)}
          >
            View
          </Button>
          <Button
            disabled={!checkPermission("Admin", "Users", "DELETE")}
            size="small"
            color="error"
            variant="outlined"
            onClick={() => handleDeleteClick(row.original.id)}
          >
            Delete
          </Button>
          <Button
            disabled={!checkPermission("Admin", "Users", "RESETPWD")}
            size="small"
			 title="Reset Password"
            color="warning"
            variant="outlined"
            onClick={() => handleResetPassword(row.original.id)}
          >
           Reset
          </Button>
        </div>
      ),
    },
  ], [handleViewDetails, handleResetPassword]);

  // ------------------- Table Instance -------------------
  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    page,
    gotoPage,
    state: { pageIndex, pageSize },
    setPageSize,
    pageCount,
  } = useTable(
    { columns, data, initialState: { pageIndex: 0, pageSize: 5 } },
    usePagination
  );

  return (
    <>
      <HeaderMenu />
      <div className="user-list">
        <Typography variant="h5" sx={{ mb: 2 }}>User Management</Typography>

        {/* ------------------- Search Filters ------------------- */}
        <Box sx={{ mb: 2 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={3}>
              <TextField
                label="Name"
                name="name"
                value={filters.name}
                onChange={handleFilterChange}
                fullWidth
                size="small"
              />
            </Grid>

           <Grid item xs={12} sm={3}>
  <TextField
    label="Role"
    name="roleId"
    value={filters.roleId}
    onChange={handleFilterChange}
    fullWidth
    size="small"
    select
  >
    <MenuItem value="">All Roles</MenuItem>
    {roles.map(role => (
      <MenuItem key={role.id} value={role.id}>
        {role.roleCode}
      </MenuItem>
    ))}
  </TextField>
</Grid>


            <Grid item xs={12} sm={3}>
  <TextField
    label="Company"
    name="companyId"
    value={filters.companyId}
    onChange={handleFilterChange}
    fullWidth
    size="small"
    select
  >
    <MenuItem value="">All Companies</MenuItem>
    {companies.map(company => (
      <MenuItem key={company.id} value={company.id}>
        {company.companyName}
      </MenuItem>
    ))}
  </TextField>
</Grid>


            <Grid item xs={12} sm={3}>
              <Button variant="contained" color="primary" fullWidth onClick={handleSearch}>
                Search
              </Button>
            </Grid>
          </Grid>
        </Box>

        {/* Create User Button */}
        <Button
          disabled={!checkPermission("Admin", "Users", "CREATE")}
          variant="contained"
          color="primary"
          sx={{ mb: 2 }}
          onClick={() => { setSelectedUser(null); setDialogOpen(true); }}
        >
          + Create User
        </Button>

        {/* Snackbar */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={4000}
          onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
          anchorOrigin={{ vertical: "top", horizontal: "center" }}
        >
          <Alert severity={snackbar.severity} variant="filled" sx={{ width: "100%" }}>
            {snackbar.message}
          </Alert>
        </Snackbar>

        {/* Table */}
        {loading ? (
          <Box sx={{ textAlign: "center", py: 4 }}>
            <CircularProgress />
            <Typography sx={{ mt: 1 }}>Loading users...</Typography>
          </Box>
        ) : ( 
		<div className="table-container">
          <table {...getTableProps()} className="table">
            <thead>
              {headerGroups.map(headerGroup => (
                <tr {...headerGroup.getHeaderGroupProps()} key={headerGroup.id}>
                  {headerGroup.headers.map(column => (
                    <th {...column.getHeaderProps()} key={column.id}>{column.render("Header")}</th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody {...getTableBodyProps()}>
              {page.map(row => {
                prepareRow(row);
                return (
                  <tr {...row.getRowProps()} key={row.original.id || row.index}>
                    {row.cells.map(cell => (
                      <td {...cell.getCellProps()} key={cell.column.id} data-label={cell.column.Header}>
                        {cell.render("Cell")}
                      </td>
                    ))}
                  </tr>
                );
              })}
            </tbody>
          </table>
		  </div>
        )}

        {/* Pagination */}
        {!loading && data.length > 0 && (
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mt: 2 }}>
            <Pagination
              count={pageCount}
              page={pageIndex + 1}
              onChange={(e, value) => gotoPage(value - 1)}
              color="primary"
              shape="rounded"
            />
            <Box>
              <label htmlFor="rows-per-page" style={{ marginRight: "8px" }}>Rows per page:</label>
              <select id="rows-per-page" value={pageSize} onChange={(e) => setPageSize(Number(e.target.value))}>
                {[5, 10, 15, 20].map(option => (<option key={option} value={option}>{option}</option>))}
              </select>
            </Box>
          </Box>
        )}

       {/* Create / Edit Dialog */}
<Dialog
  open={dialogOpen}
  onClose={(event, reason) => {
    // Only close on Escape key, not on backdrop click
    if (reason !== "backdropClick") {
      setDialogOpen(false);
    }
  }}
  fullWidth
  maxWidth="sm"
>
  <DialogTitle>{selectedUser ? "Edit User" : "Create User"}</DialogTitle>
  <DialogContent>
    <CreateUser
      user={selectedUser}
      onClose={() => setDialogOpen(false)}
      isDialog
    />
  </DialogContent>
  <DialogActions>
    <Button onClick={() => setDialogOpen(false)}>Close</Button>
  </DialogActions>
</Dialog>


        {/* Delete Confirmation */}
        <Dialog open={confirmDeleteOpen} onClose={() => setConfirmDeleteOpen(false)}>
          <DialogTitle>Confirm Delete</DialogTitle>
          <DialogContent>
            <Typography>Are you sure you want to delete this user?</Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setConfirmDeleteOpen(false)}>Cancel</Button>
            <Button onClick={confirmDelete} color="error" variant="contained">Delete</Button>
          </DialogActions>
        </Dialog>
      </div>
    </>
  );
};

export default UserListPage;
